import { X, Trash2, GitBranch, Spline } from "lucide-react";
import Field from "@/shared/components/field";
import type { EdgeKind } from "../../models/WorkflowEdge";
import type WorkflowNode from "../../models/WorkflowNode";

interface EdgeConfigPanelProps {
  condition: string;
  kind: EdgeKind;
  /** Nodo origen de la conexión: si es un Switch, la condición se elige como ruta. */
  sourceNode?: WorkflowNode;
  onChange: (data: { condition: string; kind: EdgeKind }) => void;
  onDelete: () => void;
  onClose: () => void;
}

interface SwitchRoute {
  key: string;
  name: string;
}

/** Extrae las rutas (cases + default) de las properties de un nodo Switch. */
function parseSwitchRoutes(node: WorkflowNode): SwitchRoute[] {
  const routes: SwitchRoute[] = [];
  const rawCases = node.properties?.cases;
  if (Array.isArray(rawCases)) {
    for (const c of rawCases) {
      const obj = c as Record<string, unknown>;
      const key = String(obj.key ?? "");
      if (key) routes.push({ key, name: String(obj.name ?? key) });
    }
  }
  const defaultKey = String(node.properties?.defaultCaseKey ?? "default");
  if (defaultKey && !routes.some((r) => r.key === defaultKey)) {
    routes.push({ key: defaultKey, name: "Por defecto" });
  }
  return routes;
}

/** Expresión que activa la rama del Switch para una ruta. */
function routeCondition(switchNodeId: string, caseKey: string): string {
  return `{{ nodes.${switchNodeId}.output.${caseKey} }}`;
}

export default function EdgeConfigPanel({
  condition,
  kind,
  sourceNode,
  onChange,
  onDelete,
  onClose,
}: EdgeConfigPanelProps) {
  const isSwitchSource = sourceNode?.type === "Switch";
  const routes = isSwitchSource && sourceNode ? parseSwitchRoutes(sourceNode) : [];

  // Ruta actualmente seleccionada, derivada de la condición guardada.
  const selectedRoute = isSwitchSource
    ? routes.find((r) => condition.trim() === routeCondition(sourceNode!.id, r.key))?.key ??
      (condition.trim() === "" ? "" : "__custom__")
    : "";

  return (
    <aside className="w-96 shrink-0 border-l border-black/5 bg-white/70 backdrop-blur-md overflow-y-auto">
      <div className="sticky top-0 z-10 flex items-center gap-3 px-5 py-3.5 bg-white/85 backdrop-blur-md border-b border-black/5">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent shadow-sm">
          <Spline className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-bold text-foreground truncate">Conexión</h2>
          {sourceNode && (
            <p className="text-[11px] text-foreground/40 font-medium truncate">
              Desde {sourceNode.name}
            </p>
          )}
        </div>
        <button
          onClick={onClose}
          className="flex size-8 shrink-0 items-center justify-center rounded-xl text-foreground/40 hover:bg-black/5 hover:text-foreground transition-colors cursor-pointer"
          aria-label="Cerrar"
        >
          <X className="size-4" />
        </button>
      </div>

      <div className="p-5 space-y-5">
      {isSwitchSource && (
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wider text-foreground/40 flex items-center gap-1.5">
            <GitBranch className="size-3" />
            Ruta del Switch
          </p>
          <p className="text-[11px] text-foreground/45 leading-relaxed">
            Esta conexión sale de <strong>{sourceNode!.name}</strong>: elige qué ruta la activa.
          </p>
          <select
            value={selectedRoute}
            onChange={(e) => {
              const key = e.target.value;
              if (key === "__custom__" || key === "") return;
              onChange({ condition: routeCondition(sourceNode!.id, key), kind });
            }}
            className="w-full bg-white border border-black/[0.07] rounded-lg py-2 px-2.5 text-[13px] font-medium text-foreground focus:outline-none focus:border-accent/40 focus:ring-2 focus:ring-accent/10 cursor-pointer"
          >
            <option value="" disabled>
              Selecciona una ruta…
            </option>
            {routes.map((r) => (
              <option key={r.key} value={r.key}>
                {r.name} ({r.key})
              </option>
            ))}
            {selectedRoute === "__custom__" && (
              <option value="__custom__">Condición personalizada</option>
            )}
          </select>
          {routes.length === 0 && (
            <p className="text-[11px] text-amber-600">
              El Switch no tiene rutas definidas todavía: configúralas en el nodo primero.
            </p>
          )}
        </div>
      )}

      <Field
        label={isSwitchSource ? "Condición (generada por la ruta)" : "Condición"}
        name="condition"
        value={condition}
        onChange={(e) => onChange({ condition: e.target.value, kind })}
        placeholder="Expresión opcional (vacío = siempre)"
      />

      <Field
        label="Tipo"
        name="kind"
        type="select"
        value={kind}
        onChange={(e) => onChange({ condition, kind: e.target.value as EdgeKind })}
        options={[
          { value: "Normal", label: "Normal" },
          { value: "OnError", label: "Al fallar (OnError)" },
        ]}
      />

      <button
        onClick={onDelete}
        className="w-full h-10 rounded-xl border border-red-500/20 bg-red-500/5 text-red-600 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-red-500 hover:border-red-500 hover:text-white transition-all cursor-pointer"
      >
        <Trash2 className="size-4" />
        Eliminar conexión
      </button>
      </div>
    </aside>
  );
}
